import { Description, UploadFile, Visibility } from "@mui/icons-material";
import { Button, Chip, TextField } from "@mui/material";
import { ChangeEvent, useEffect, useRef, useState } from "react";

import PreloadFb from "../../../components/Preload";
import PreloadButton from "../../../components/PreloadButton";
import { getApiErrorMessage } from "../../../helpers/api/getApiErrorMessage";
import { defaultToast } from "../../../helpers/toast/defaultToast";
import { api } from "../../../services/api";
import { ContractsContentStyled } from "./styles";
import {
    CurrentCustomerContract,
    CustomerBucketContract,
    CustomerBucketContractList,
    CustomerBucketContractLog,
} from "./types";

interface Props {
    customerId?: number;
}

function formatContractDate(value?: string | null): string {
    if (!value) {
        return "data não informada";
    }

    const data = new Date(value);
    if (Number.isNaN(data.getTime())) {
        return "data não informada";
    }

    const dia = String(data.getDate()).padStart(2, "0");
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const ano = data.getFullYear();
    const hora = String(data.getHours()).padStart(2, "0");
    const minutos = String(data.getMinutes()).padStart(2, "0");

    return `${dia}/${mes}/${ano} às ${hora}:${minutos}`;
}

function getCurrentContractSignedLabel(
    contract: CurrentCustomerContract
): string {
    if (contract.is_signed) {
        return "Assinado";
    }

    if (contract.signed_count > 0) {
        return "Parcialmente assinado";
    }

    return "Não assinado";
}

function getCurrentContractSignedColor(
    contract: CurrentCustomerContract
): "success" | "warning" | "default" {
    if (contract.is_signed) {
        return "success";
    }

    if (contract.signed_count > 0) {
        return "warning";
    }

    return "default";
}

function getCurrentContractSignedMeta(
    contract: CurrentCustomerContract
): string {
    if (contract.is_signed) {
        return `Assinado em ${formatContractDate(contract.signed_at)}`;
    }

    if (contract.signatories_count > 0) {
        return `Assinaturas: ${contract.signed_count}/${contract.signatories_count}`;
    }

    return "Ainda não há signatários neste contrato";
}

function ContractsContent({ customerId }: Props) {
    const uploadInputRef = useRef<HTMLInputElement>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [isReplacing, setIsReplacing] = useState(false);
    const [contracts, setContracts] = useState<CustomerBucketContract[]>([]);
    const [logs, setLogs] = useState<CustomerBucketContractLog[]>([]);
    const [currentContract, setCurrentContract] =
        useState<CurrentCustomerContract | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [contractName, setContractName] = useState("");
    const [replacingContractId, setReplacingContractId] = useState<number>();

    const applyList = (list?: CustomerBucketContractList) => {
        setCurrentContract(list?.current_contract ?? null);
        setContracts(list?.contracts ?? []);
        setLogs(list?.logs ?? []);
    };

    const fetchContracts = async () => {
        if (!customerId) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await api.get(
                `/customers/${customerId}/contracts`
            );
            applyList(response.data.response);
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(error, "Erro ao carregar contratos")
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchContracts();
    }, [customerId]);

    const handleSelectUploadFile = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        setSelectedFile(file);
        if (file && !contractName) {
            setContractName(file.name);
        }
    };

    const handleUpload = async () => {
        if (!customerId || !selectedFile) {
            defaultToast("error", "Selecione um arquivo de contrato");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        if (contractName.trim()) {
            formData.append("name", contractName.trim());
        }

        setIsUploading(true);
        try {
            const response = await api.post(
                `/customers/${customerId}/contracts`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            applyList(response.data.response);
            setSelectedFile(null);
            setContractName("");
            if (uploadInputRef.current) {
                uploadInputRef.current.value = "";
            }
            defaultToast(
                "success",
                response.data.message || "Contrato enviado com sucesso"
            );
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(error, "Erro ao enviar contrato")
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleOpenReplace = (contractId: number) => {
        setReplacingContractId(contractId);
        replaceInputRef.current?.click();
    };

    const handleReplaceFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const contractId = replacingContractId;

        if (replaceInputRef.current) {
            replaceInputRef.current.value = "";
        }

        if (!customerId || !file || !contractId) {
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setIsReplacing(true);
        try {
            const response = await api.post(
                `/customers/${customerId}/contracts/${contractId}/replace`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            applyList(response.data.response);
            defaultToast(
                "success",
                response.data.message || "Contrato substituído com sucesso"
            );
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(error, "Erro ao substituir contrato")
            );
        } finally {
            setIsReplacing(false);
            setReplacingContractId(undefined);
        }
    };

    if (isLoading) {
        return <PreloadFb />;
    }

    return (
        <ContractsContentStyled>
            <div className="titlePageContainer">
                <h2>Contratos</h2>
            </div>

            <h4>Contrato atual</h4>
            {currentContract ? (
                <div className="contractList">
                    <div className="contractCard current">
                        <div className="contractInfo">
                            <div className="currentBadge">
                                <Chip
                                    label="Contrato atual"
                                    color="primary"
                                    size="small"
                                />
                                <Chip
                                    label={getCurrentContractSignedLabel(
                                        currentContract
                                    )}
                                    color={getCurrentContractSignedColor(
                                        currentContract
                                    )}
                                    size="small"
                                />
                            </div>
                            <div className="contractName">
                                <Description fontSize="small" />
                                {currentContract.name}
                            </div>
                            <span className="contractMeta">
                                Gerado em{" "}
                                {formatContractDate(
                                    currentContract.updated_at ||
                                        currentContract.created_at
                                )}
                            </span>
                            <span className="contractMeta">
                                {getCurrentContractSignedMeta(currentContract)}
                            </span>
                            {currentContract.signatories.length > 0 && (
                                <ul className="signatoriesList">
                                    {currentContract.signatories.map(
                                        (signatory) => (
                                            <li key={signatory.id}>
                                                {signatory.name}:{" "}
                                                {signatory.is_signed
                                                    ? `assinou em ${formatContractDate(
                                                          signatory.signed_at
                                                      )}`
                                                    : "pendente"}
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                        <div className="contractActions">
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<Visibility />}
                                onClick={() =>
                                    window.open(
                                        currentContract.file_url,
                                        "_blank",
                                        "noopener,noreferrer"
                                    )
                                }
                            >
                                Visualizar
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <p className="emptyState">
                    Este cliente ainda não possui um contrato atual da proposta.
                </p>
            )}

            <div className="uploadCard">
                <h4>Enviar novo contrato</h4>
                <div className="uploadRow">
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFile />}
                    >
                        Selecionar arquivo
                        <input
                            ref={uploadInputRef}
                            hidden
                            type="file"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                            onChange={handleSelectUploadFile}
                        />
                    </Button>
                    <span className="fileName">
                        {selectedFile
                            ? selectedFile.name
                            : "Nenhum arquivo selecionado"}
                    </span>
                </div>
                <TextField
                    size="small"
                    label="Nome do contrato"
                    value={contractName}
                    onChange={(event) => setContractName(event.target.value)}
                    placeholder="Ex: Contrato social, aditivo..."
                />
                <PreloadButton
                    colorText="white"
                    background="var(--confirm)"
                    loading={isUploading}
                    text="Enviar contrato"
                    type="button"
                    onClick={handleUpload}
                    disabledButton={!selectedFile || isUploading}
                    sx={{ alignSelf: "flex-start", minWidth: "180px" }}
                />
            </div>

            <h4>Contratos enviados</h4>
            {contracts.length === 0 ? (
                <p className="emptyState">
                    Nenhum contrato enviado para este cliente.
                </p>
            ) : (
                <div className="contractList">
                    {contracts.map((contract) => (
                        <div className="contractCard" key={contract.id}>
                            <div className="contractInfo">
                                <div className="contractName">
                                    <Description fontSize="small" />
                                    {contract.name}
                                </div>
                                <span className="contractMeta">
                                    Última alteração:{" "}
                                    {formatContractDate(
                                        contract.last_change?.date
                                    )}{" "}
                                    por{" "}
                                    {contract.last_change?.user?.name ||
                                        "usuário não identificado"}{" "}
                                    ({contract.last_change?.action_label})
                                </span>
                            </div>
                            <div className="contractActions">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() =>
                                        window.open(
                                            contract.file_url,
                                            "_blank",
                                            "noopener,noreferrer"
                                        )
                                    }
                                >
                                    Visualizar
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<UploadFile />}
                                    disabled={isReplacing}
                                    onClick={() =>
                                        handleOpenReplace(contract.id)
                                    }
                                >
                                    Substituir
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <input
                ref={replaceInputRef}
                className="hiddenInput"
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                onChange={handleReplaceFile}
            />

            <h4 className="logsTitle">Log de alterações</h4>
            {logs.length === 0 ? (
                <p className="emptyState">Nenhuma alteração registrada.</p>
            ) : (
                <div className="logsList">
                    {logs.map((log) => (
                        <div className="logItem" key={log.id}>
                            <span className="logDate">
                                {formatContractDate(log.created_at)}
                            </span>
                            {log.user?.name || "usuário não identificado"} —{" "}
                            {log.description}
                        </div>
                    ))}
                </div>
            )}
        </ContractsContentStyled>
    );
}

export default ContractsContent;
